import moment from 'moment';
import settings from '/@/core/settings';
import { assignYearsToDateRange } from '/@/utils/date';

// Planting Dates

/**
 * Planting windows for a crop, from whatever zones the caller has.
 *
 * Usually those are the selected field's, but the product-first wizard asks for a planting date
 * before any field is chosen and falls back to the product's own zones — same shape, same rules.
 */
export const getValidPlantingDatesFromZones = (
    zones: { type: string; minDate: string; maxDate: string }[] | undefined,
    crop: string
): [string, string][] | undefined =>
    zones
        ?.filter((zone) => zone.type === settings.enums.zoneTypes?.[crop]?.value)
        .map((zone) => {
            // Use smart year assignment for both min and max dates
            const assignedDates = assignYearsToDateRange(zone.minDate, zone.maxDate);

            if (!assignedDates) {
                return null;
            }

            return [assignedDates.minDate, assignedDates.maxDate];
        })
        .filter(Boolean) // Filter out null values
        .filter(([min, max]) => min && max) as [string, string][]; // Filter out invalid dates

export const getValidPlantingDates = (cropConfig, field) => {
    if (!cropConfig) return;

    return getValidPlantingDatesFromZones(field?.node.zones, cropConfig.crop);
};

export const getCropConfig = (recommendation, crop) => {
    if (!recommendation) return {};
    const config = recommendation.crops.find((c) => c.crop === crop);
    if (!config) return;

    // Calculate user all available environments
    const { environment, userWorseEnvironment, userBetterEnvironment } = config;
    config.betterEnvironment = environment * (1 + userBetterEnvironment / 100);
    config.worseEnvironment = environment * (1 - userWorseEnvironment / 100);

    return config;
};

export const getMetricChoices = (products) => {
    const availableMetrics = [];
    products.forEach(({ metrics }) => {
        metrics?.forEach((metric) => {
            if (metric.showInProductSelection && !availableMetrics.find(({ id }) => metric.id === id)) {
                availableMetrics.push(metric);
            }
        });
    });
    return availableMetrics;
};

// Recommendation

const serializeDate = (date) => {
    // Note: Week is determined based on day count (1 week = 7 days) and not based on day name
    const week = Math.floor((date.date() - 1) / (date.daysInMonth() / 4)) + 1;
    const month = date.month() + 1;
    return { week, month, year: date.year() };
};

export const getShowPlantingDateWarning = (products, cropConfig) => {
    if (!cropConfig) return false;
    const { crop, plantingDate } = cropConfig;

    if (!products.length || crop === 'corn') return false;

    const current = serializeDate(moment());
    const selected = serializeDate(moment(plantingDate));

    const plantingDates = products.reduce(
        (dates, product) => [...dates, ...product.plantingDates.filter(({ value }) => ['G', 'Y'].includes(value))],
        []
    );

    const forSelected = plantingDates.find((pd) => pd.week === selected.week && pd.month === selected.month);
    if (forSelected) return false;

    const upcoming = plantingDates.filter((pd) => pd.week >= current.week && pd.month >= current.month);
    return !!upcoming.length;
};
